import { formatDistanceToNow } from 'date-fns';

import { convertDateISOStringToTimestamp } from './timestamp';

/**
 * Formats a date for notification UIs as a relative distance to now (e.g. "3 minutes ago").
 * Falls back to an empty string if the input is invalid.
 *
 * @param date - Date instance, ISO string or timestamp to format.
 * @returns A human-readable relative time string.
 */
export const formatNotificationDate = (
    date: Date | string | number,
): string => {
    if (!date) return '';

    let parsedDate: Date;

    if (date instanceof Date) {
        parsedDate = date;
    } else if (typeof date === 'string') {
        parsedDate = new Date(convertDateISOStringToTimestamp(date));
    } else {
        parsedDate = new Date(date);
    }

    if (Number.isNaN(parsedDate.getTime())) {
        return '';
    }

    return formatDistanceToNow(parsedDate, { addSuffix: true });
};

/**
 * Formats a date as dd/mm/yyyy
 *
 * @param dateString - ISO string.
 * @returns A date with dd/mm/yyyy format.
 */
export const formatPatternDate = (dateString: string): string => {
    const date = new Date(dateString);

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
};

/**
 * Formats a date for device last seen display
 *
 * @param dateString - ISO string.
 * @returns A localized date and time string.
 */
export const formatDeviceLastSeen = (dateString: string): string => {
    const date = new Date(dateString);

    return date.toLocaleDateString(navigator.language, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};
